import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { ChevronDown, ChevronRight, Download, Loader2, Plus, Settings2, Upload, Wallet, X } from "lucide-react"
import { api_fetch } from "../../../api/client"
import { exactCurrencyFormatter } from "../../../utils"
import { download_export } from "../../../utils/download"
import { Button } from "../../../components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { toast } from "../../../components/ui/use-toast"
import { AccrualBucketsModal } from "./accrual_buckets_modal"
import { AccrualSweepModal, sweep_target } from "./accrual_sweep_modal"
import { AccrualAddRowModal } from "./accrual_add_row_modal"
import {
  accrual_bank_report,
  accrual_cell,
  accrual_line_item,
  account_row,
  added_row,
  bank_view,
  bucket_row,
  cell_value,
  line_items_for,
  merge_added_rows,
  MONTH_SHORT,
  month_iso,
  parse_amount_input,
  sweeps_for,
} from "./accrual_bank_model"

const VIEWS: { key: bank_view; label: string; hint: string }[] = [
  { key: "plan", label: "Accrued", hint: "What was accrued for the month (plan plus any sweeps). Click a customer cell to edit it." },
  { key: "actual", label: "Deductions", hint: "What MarginWiz has coded to the cell, by posting date (check date when there is none)" },
  { key: "remaining", label: "Remaining", hint: "Accrued minus deductions: what is still in the bank" },
]

interface drill_target {
  account_id: string | null
  /** undefined = the GL row (all buckets); null = the "(no bucket)" row. */
  bucket_id: string | null | undefined
  month_index: number
}

function money(value: number) {
  return exactCurrencyFormatter(value)
}

function value_class(value: number, view: bank_view): string {
  if (value === 0) return "text-gray-300"
  if (view === "remaining") return value < 0 ? "text-red-600" : "text-gray-900"
  return "text-gray-900"
}

export function AccrualBankPage({ className }: { className?: string; params?: unknown } = {}) {
  const current_year = new Date().getFullYear()
  const [year, setYear] = useState(current_year)
  const [view, setView] = useState<bank_view>("remaining")
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [drill, setDrill] = useState<drill_target | null>(null)
  const [buckets_open, setBucketsOpen] = useState(false)
  const [add_row_open, setAddRowOpen] = useState<{ account_id: string | null } | null>(null)
  const [added, setAdded] = useState<added_row[]>([])
  const [sweep, setSweep] = useState<sweep_target | null>(null)
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const file_input = useRef<HTMLInputElement>(null)

  const { data, isLoading, mutate } = useSWR(["accrual-bank", year], () => api_fetch<accrual_bank_report>("/accrual-bank", { params: { year } }))
  const report = data?.ok ? data.value.data : null

  const year_options = [current_year - 2, current_year - 1, current_year, current_year + 1]
  const rows = useMemo(() => (report ? merge_added_rows(report.rows, added, report.accounts, report.buckets) : []), [report, added])
  const bucket_by_id = useMemo(() => new Map((report?.buckets ?? []).map((b) => [b.id, b])), [report])

  function toggle_expanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggle_drill(target: drill_target) {
    setDrill((prev) =>
      prev && prev.account_id === target.account_id && prev.bucket_id === target.bucket_id && prev.month_index === target.month_index ? null : target
    )
  }

  async function save_plan(bucket_id: string, account_id: string, month_index: number, amount: number) {
    const res = await api_fetch("/accrual-bank/entries", {
      method: "POST",
      body: { entries: [{ bucket_id, account_id, month: month_iso(year, month_index), kind: "plan", amount }] },
    })
    if (!res.ok) {
      toast({ title: "Could not save the accrual", description: res.error.message, variant: "destructive" })
      return false
    }
    mutate()
    return true
  }

  function add_row(account_id: string, bucket_id: string) {
    setAdded((prev) => (prev.some((r) => r.account_id === account_id && r.bucket_id === bucket_id) ? prev : [...prev, { account_id, bucket_id }]))
    setExpanded((prev) => new Set(prev).add(account_id))
    setView("plan")
    setAddRowOpen(null)
  }

  async function handle_export() {
    setExporting(true)
    try {
      await download_export(`/api/accrual-bank/export?year=${year}`, `trade_accrual_bank_${year}.xlsx`)
    } catch {
      toast({ title: "Export failed", variant: "destructive" })
    }
    setExporting(false)
  }

  async function handle_import(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setImporting(true)
    try {
      const form = new FormData()
      form.append("file", file)
      form.append("year", String(year))
      const response = await fetch("/api/accrual-bank/import", { method: "POST", credentials: "same-origin", body: form })
      const body = await response.json().catch(() => null)
      if (!response.ok) {
        toast({ title: "Import failed", description: body?.message ?? response.statusText, variant: "destructive" })
      } else {
        const created = body.buckets_created?.length ? ` New buckets: ${body.buckets_created.join(", ")}.` : ""
        toast({
          title: `Imported ${body.entries_written} accrual amounts from ${body.rows_read} rows`,
          description: `${created}${body.errors?.length ? ` ${body.errors.length} rows skipped: ${body.errors.slice(0, 3).join("; ")}` : ""}`,
          variant: body.errors?.length ? "destructive" : undefined,
        })
        mutate()
      }
    } catch (e) {
      toast({ title: "Import failed", description: String(e), variant: "destructive" })
    }
    setImporting(false)
    if (file_input.current) file_input.current.value = ""
  }

  const editable = view === "plan"

  return (
    <div class={`space-y-5 ${className ?? ""}`}>
      <div class="flex flex-wrap items-start gap-4">
        <div class="min-w-0">
          <h1 class="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <span class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Wallet className="h-4 w-4" />
            </span>
            Trade accrual bank
          </h1>
          <p class="mt-1 text-sm text-gray-500 max-w-2xl">
            Your monthly accrual per customer and GL account, drawn down by the deductions coded in MarginWiz. Type the accrual into a
            cell, upload your accrual sheet, and sweep what will not be used.
          </p>
        </div>
        <div class="flex items-center gap-2 ml-auto flex-wrap">
          <Select value={String(year)} onValueChange={(value: string) => setYear(Number(value))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {year_options.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setBucketsOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" />
            Buckets
          </Button>
          <input
            ref={file_input}
            type="file"
            accept=".xlsx,.xls,.csv"
            class="hidden"
            onChange={(e: Event) => handle_import((e.target as HTMLInputElement).files)}
          />
          <Button variant="outline" onClick={() => file_input.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload sheet
          </Button>
          <Button variant="outline" onClick={handle_export} disabled={exporting || !report}>
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export
          </Button>
          <Button onClick={() => setAddRowOpen({ account_id: null })} disabled={!report || report.buckets.length === 0}>
            <Plus className="h-4 w-4 mr-2" />
            Add row
          </Button>
        </div>
      </div>

      {isLoading && (
        <div class="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Loading…
        </div>
      )}
      {!isLoading && !report && <div class="text-sm text-red-600 py-8">Failed to load the accrual bank.</div>}

      {!isLoading && report && (
        <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div class="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100">
            <div class="inline-flex rounded-lg bg-gray-100 p-0.5">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  title={v.hint}
                  class={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    view === v.key ? "bg-white text-primary shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                  onClick={() => setView(v.key)}>
                  {v.label}
                </button>
              ))}
            </div>
            <p class="text-xs text-gray-500">{VIEWS.find((v) => v.key === view)?.hint}</p>
            <div class="ml-auto flex items-center gap-4 text-xs text-gray-500">
              <span>
                Accrued <span class="font-semibold text-gray-900 tabular-nums">{money(cell_value(report.grand_total.total, "plan"))}</span>
              </span>
              <span>
                Deductions <span class="font-semibold text-gray-900 tabular-nums">{money(report.grand_total.total.actual)}</span>
              </span>
              <span>
                Remaining{" "}
                <span class={`font-semibold tabular-nums ${cell_value(report.grand_total.total, "remaining") < 0 ? "text-red-600" : "text-primary"}`}>
                  {money(cell_value(report.grand_total.total, "remaining"))}
                </span>
              </span>
            </div>
          </div>

          {report.buckets.length === 0 && (
            <div class="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
              No customer buckets yet. Upload your accrual sheet to create them from its customer tabs, or add them under Buckets.
              Until a source has an "All Other" bucket, its deductions show under "(no bucket)".
            </div>
          )}
          {report.unbucketed_count > 0 && report.buckets.length > 0 && (
            <div class="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
              {report.unbucketed_count} coded lines belong to a source with no bucket. Add an "All Other" bucket for that source under
              Buckets to place them.
            </div>
          )}

          {report.awaiting_validation_count > 0 && (
            <p class="mx-5 mt-4 text-sm text-gray-500">
              {report.awaiting_validation_count} coded lines ({money(report.awaiting_validation_amount)}) are waiting on validation. They join
              these balances once the deduction is validated.
            </p>
          )}

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th class="text-left font-medium px-5 py-3 whitespace-nowrap sticky left-0 bg-white z-[1] min-w-[260px]">GL account / customer</th>
                  {MONTH_SHORT.map((m) => (
                    <th key={m} class="text-right font-medium px-3 py-3 whitespace-nowrap">
                      {m}
                    </th>
                  ))}
                  <th class="text-right font-medium px-5 py-3 whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={14} class="px-5 py-12 text-center text-gray-500">
                      Nothing accrued or coded in {year} yet. Use <span class="font-medium text-gray-700">Add row</span> to start a GL account.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const key = row.account_id ?? "uncoded"
                  const is_open = expanded.has(key)
                  return (
                    <>
                      <tr key={key} class="border-b border-gray-100 bg-white hover:bg-gray-50/70">
                        <td class="px-5 py-2.5 whitespace-nowrap font-medium text-gray-900 sticky left-0 bg-white z-[1]">
                          <button type="button" class="inline-flex items-center gap-1.5" onClick={() => toggle_expanded(key)}>
                            {is_open ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            {row.account_label}
                          </button>
                        </td>
                        <MonthCells
                          months={row.months}
                          total={row.total}
                          view={view}
                          bold
                          active_month={drill && drill.account_id === row.account_id && drill.bucket_id === undefined ? drill.month_index : null}
                          onClick={(month_index: number) => toggle_drill({ account_id: row.account_id, bucket_id: undefined, month_index })}
                        />
                      </tr>
                      {is_open &&
                        row.buckets.map((b) => (
                          <tr key={`${key}-${b.bucket_id ?? "none"}-${b.rollup}`} class="border-b border-gray-100 bg-gray-50/60 hover:bg-primary/5">
                            <td class="pl-11 pr-5 py-2 whitespace-nowrap text-gray-700 sticky left-0 bg-gray-50 z-[1]">
                              <span class="text-gray-400">{b.rollup} /</span> {b.bucket_name}
                              {b.bucket_id && row.account_id && (
                                <button
                                  type="button"
                                  class="ml-2 text-xs font-medium text-primary/80 hover:text-primary"
                                  title="Record a sweep (release or top up accrual) for this customer and GL"
                                  onClick={() => setSweep({ bucket_id: b.bucket_id!, account_id: row.account_id!, year, month_index: new Date().getMonth() })}>
                                  sweep
                                </button>
                              )}
                            </td>
                            {editable && b.bucket_id && row.account_id ? (
                              <EditableMonthCells
                                months={b.months}
                                total={b.total}
                                onSave={(month_index: number, amount: number) => save_plan(b.bucket_id!, row.account_id!, month_index, amount)}
                              />
                            ) : (
                              <MonthCells
                                months={b.months}
                                total={b.total}
                                view={view}
                                active_month={
                                  drill && drill.account_id === row.account_id && drill.bucket_id === b.bucket_id ? drill.month_index : null
                                }
                                onClick={(month_index: number) => toggle_drill({ account_id: row.account_id, bucket_id: b.bucket_id, month_index })}
                              />
                            )}
                          </tr>
                        ))}
                      {is_open && row.account_id && report.buckets.length > 0 && (
                        <tr class="border-b border-gray-100 bg-gray-50/60">
                          <td colSpan={14} class="pl-11 pr-5 py-1.5 sticky left-0">
                            <button type="button" class="text-xs font-medium text-primary/80 hover:text-primary inline-flex items-center gap-1" onClick={() => setAddRowOpen({ account_id: row.account_id })}>
                              <Plus className="h-3 w-3" />
                              Add customer
                            </button>
                          </td>
                        </tr>
                      )}
                      {drill && drill.account_id === row.account_id && (
                        <tr class="border-b border-gray-100 bg-primary/5">
                          <td colSpan={14} class="px-5 py-4">
                            <DrillPanel
                              report={report}
                              target={drill}
                              row={row}
                              bucket={drill.bucket_id ? row.buckets.find((b) => b.bucket_id === drill.bucket_id) : undefined}
                              bucket_label={drill.bucket_id ? bucket_by_id.get(drill.bucket_id)?.name ?? null : drill.bucket_id === null ? "(no bucket)" : null}
                              onClose={() => setDrill(null)}
                              onDeleteSweep={async (id: string) => {
                                await api_fetch(`/accrual-bank/entries/${id}`, { method: "DELETE" })
                                mutate()
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr class="bg-gray-50 font-semibold text-gray-900 border-t border-gray-200">
                    <td class="px-5 py-3 sticky left-0 bg-gray-50 z-[1]">Total</td>
                    <MonthCells months={report.grand_total.months} total={report.grand_total.total} view={view} bold active_month={null} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <p class="px-5 py-3 text-xs text-gray-500 border-t border-gray-100">
            {editable
              ? "Click a customer cell to type the month's accrual; it saves when you leave the cell. Sweeps stay separate and are shown in the cell's tooltip."
              : "Click a month to see the coded deductions behind it. Red means more was deducted than accrued."}
          </p>
        </div>
      )}

      <AccrualBucketsModal open={buckets_open} onClose={() => setBucketsOpen(false)} onChanged={() => mutate()} />
      {sweep && report && (
        <AccrualSweepModal
          target={sweep}
          buckets={report.buckets}
          accounts={report.accounts}
          onClose={() => setSweep(null)}
          onSaved={() => {
            setSweep(null)
            mutate()
          }}
        />
      )}
      {add_row_open && report && (
        <AccrualAddRowModal
          accounts={report.accounts}
          buckets={report.buckets}
          fixed_account_id={add_row_open.account_id}
          existing={rows.flatMap((r) => r.buckets.filter((b) => b.bucket_id).map((b) => ({ account_id: r.account_id ?? "", bucket_id: b.bucket_id! })))}
          onClose={() => setAddRowOpen(null)}
          onAdd={add_row}
        />
      )}
    </div>
  )
}

function MonthCells({
  months,
  total,
  view,
  bold,
  active_month,
  onClick,
}: {
  months: accrual_cell[]
  total: accrual_cell
  view: bank_view
  bold?: boolean
  active_month: number | null
  onClick?: (month_index: number) => void
}) {
  return (
    <>
      {months.map((m, i) => {
        const value = cell_value(m, view)
        return (
          <td key={i} class={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${bold ? "font-medium" : ""}`}>
            <button
              type="button"
              disabled={!onClick}
              title={view === "plan" && m.swept !== 0 ? `Plan ${money(m.planned)}, swept ${money(m.swept)}` : undefined}
              class={`rounded px-1 -mx-1 ${value_class(value, view)} ${active_month === i ? "bg-primary/10 text-primary" : ""} ${
                onClick ? "hover:bg-primary/10" : ""
              }`}
              onClick={() => onClick?.(i)}>
              {value === 0 ? "–" : money(value)}
            </button>
          </td>
        )
      })}
      <td class={`px-5 py-2 text-right tabular-nums whitespace-nowrap ${bold ? "font-semibold" : ""} ${value_class(cell_value(total, view), view)}`}>
        {money(cell_value(total, view))}
      </td>
    </>
  )
}

/** The accrued view's bucket cells: click to type the month's plan. */
function EditableMonthCells({
  months,
  total,
  onSave,
}: {
  months: accrual_cell[]
  total: accrual_cell
  onSave: (month_index: number, amount: number) => Promise<boolean>
}) {
  return (
    <>
      {months.map((m, i) => (
        <EditablePlanCell key={i} cell={m} onSave={(amount: number) => onSave(i, amount)} />
      ))}
      <td class="px-5 py-2 text-right tabular-nums whitespace-nowrap font-medium text-gray-900">{money(cell_value(total, "plan"))}</td>
    </>
  )
}

/**
 * One editable plan cell. Keeps its own text while editing, so typing never fights the SWR
 * refresh; the saved value comes back through the report.
 */
function EditablePlanCell({ cell, onSave }: { cell: accrual_cell; onSave: (amount: number) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState("")
  const [saving, setSaving] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing) input.current?.focus()
  }, [editing])
  const shown = cell_value(cell, "plan")

  function start() {
    setText(cell.planned === 0 ? "" : String(cell.planned))
    setEditing(true)
  }

  async function commit() {
    if (!editing) return
    setEditing(false)
    const amount = parse_amount_input(text)
    if (amount === null) {
      toast({ title: "Not a number", description: `"${text}" could not be read as an amount.`, variant: "destructive" })
      return
    }
    if (amount === cell.planned) return
    setSaving(true)
    await onSave(amount)
    setSaving(false)
  }

  return (
    <td class="px-1.5 py-1 text-right tabular-nums whitespace-nowrap">
      {editing ? (
        <input
          ref={input}
          type="text"
          inputMode="decimal"
          value={text}
          class="w-24 rounded-md border border-primary bg-white px-2 py-1 text-right text-sm tabular-nums outline-none ring-2 ring-primary/20"
          onInput={(e: Event) => setText((e.target as HTMLInputElement).value)}
          onBlur={commit}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur()
            if (e.key === "Escape") {
              setEditing(false)
            }
          }}
        />
      ) : (
        <button
          type="button"
          title={cell.swept !== 0 ? `Plan ${money(cell.planned)}, swept ${money(cell.swept)}. Click to edit the plan.` : "Click to edit"}
          class={`w-24 rounded-md border border-transparent px-2 py-1 text-right hover:border-primary/40 hover:bg-white ${
            shown === 0 ? "text-gray-300" : "text-gray-900"
          } ${saving ? "opacity-50" : ""}`}
          onClick={start}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : shown === 0 ? "–" : money(shown)}
        </button>
      )}
    </td>
  )
}

function DrillPanel({
  report,
  target,
  row,
  bucket,
  bucket_label,
  onClose,
  onDeleteSweep,
}: {
  report: accrual_bank_report
  target: drill_target
  row: account_row
  bucket: bucket_row | undefined
  bucket_label: string | null
  onClose: () => void
  onDeleteSweep: (id: string) => Promise<void>
}) {
  const items: accrual_line_item[] = line_items_for(report.line_items, target.account_id, target.bucket_id, target.month_index)
  const cell = (bucket ?? row).months[target.month_index]
  const sweeps = bucket && bucket.bucket_id && row.account_id ? sweeps_for(report.entries, bucket.bucket_id, row.account_id, target.month_index) : []
  return (
    <div class="space-y-3">
      <div class="flex items-center gap-3">
        <div class="font-medium text-gray-900">
          {row.account_label}
          {bucket_label ? <span class="text-gray-500"> · {bucket_label}</span> : ""} · {MONTH_SHORT[target.month_index]} {report.year}
        </div>
        <div class="text-gray-600 text-xs tabular-nums">
          accrued {money(cell.planned)}
          {cell.swept !== 0 ? ` · swept ${money(cell.swept)}` : ""} · deductions {money(cell.actual)} · remaining{" "}
          <span class={cell_value(cell, "remaining") < 0 ? "text-red-600 font-medium" : "text-primary font-medium"}>{money(cell_value(cell, "remaining"))}</span>
        </div>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      {sweeps.length > 0 && (
        <ul class="text-xs text-gray-700 space-y-1">
          {sweeps.map((s) => (
            <li key={s.id} class="flex items-center gap-2">
              <span class="font-medium">Sweep {money(s.amount)}</span>
              {s.note && <span class="text-gray-500">— {s.note}</span>}
              <button type="button" class="text-red-600 hover:underline" onClick={() => onDeleteSweep(s.id)}>
                remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {items.length === 0 ? (
        <div class="text-sm text-gray-500">No coded deductions in this cell.</div>
      ) : (
        <div class="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <table class="text-xs w-full">
            <thead>
              <tr class="text-gray-500 bg-gray-50 border-b border-gray-200">
                <th class="text-left font-medium py-2 px-3">Invoice</th>
                <th class="text-left font-medium py-2 px-3">Source</th>
                <th class="text-left font-medium py-2 px-3">Retailer on split</th>
                <th class="text-left font-medium py-2 px-3">Month</th>
                <th class="text-right font-medium py-2 px-3">Amount</th>
                <th class="text-left font-medium py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.split_id} class="border-t border-gray-100">
                  <td class="py-1.5 px-3">
                    <a href={`/deduction/${item.deduction_id}`} target="_blank" rel="noreferrer" class="text-primary hover:underline">
                      {item.invoice_number}
                    </a>
                  </td>
                  <td class="py-1.5 px-3">{item.source}</td>
                  <td class="py-1.5 px-3">{item.retailer_name ?? <span class="text-gray-400">—</span>}</td>
                  <td class="py-1.5 px-3">{format(parseISO(item.month), "MMM yyyy")}</td>
                  <td class="py-1.5 px-3 text-right tabular-nums">{money(item.amount)}</td>
                  <td class="py-1.5 px-3">{item.validated ? "Validated" : "Open"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
